import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, MapPin, CalendarDays, Star, Ticket, Heart, Compass, Map as MapIcon, ArrowRight, Smartphone } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { SearchBar } from "@/components/SearchBar";
import { CategoryChip } from "@/components/CategoryChip";
import { SpotCard } from "@/components/SpotCard";
import { EventCard } from "@/components/EventCard";
import { SectionHeader } from "@/components/SectionHeader";
import { BenefitCard } from "@/components/BenefitCard";
import { AdBanner } from "@/components/AdBanner";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_IMAGE = "/hero-banner.png";

// Résout une URL de bannière : relative /api/files/… → backend, sinon telle quelle
const resolveHeroUrl = (url) =>
  url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const BENEFITS = [
  { icon: MapPin, color: "#2563EB", bg: "#EFF6FF", title: "Près de toi", desc: "Découvre les meilleurs endroits près de ta position." },
  { icon: Star, color: "#F59E0B", bg: "#FFFBEB", title: "Les meilleurs spots", desc: "Sélectionnés et notés par la communauté GoSpot." },
  { icon: Ticket, color: "#9333EA", bg: "#FAF5FF", title: "Événements exclusifs", desc: "Accède à des événements uniques et locaux." },
  { icon: Heart, color: "#EC4899", bg: "#FDF2F8", title: "Tes favoris", desc: "Enregistre tes coups de cœur et retrouve-les facilement." },
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const QUARTIERS = [
  {
    name: "Saint-Roch",
    desc: "Le quartier branché",
    image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?q=80&w=800&auto=format&fit=crop",
  },
  {
    name: "Vieux-Québec",
    desc: "Le cœur historique",
    image: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=800&auto=format&fit=crop",
  },
  {
    name: "Grande Allée",
    desc: "La vie nocturne",
    image: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?q=80&w=800&auto=format&fit=crop",
  },
];

export default function HomePage() {
  const [trending, setTrending] = useState([]);
  const [near, setNear] = useState([]);
  const [events, setEvents] = useState([]);
  const [allSpots, setAllSpots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroImage, setHeroImage] = useState(HERO_IMAGE);
  const [heroImageMobile, setHeroImageMobile] = useState("");
  const { t, lang } = useLanguage();

  // Bannière personnalisée par l'admin (repli sur l'image par défaut)
  useEffect(() => {
    api
      .get("/settings/hero-image")
      .then((r) => {
        if (r.data?.url) setHeroImage(resolveHeroUrl(r.data.url));
        if (r.data?.url_mobile) setHeroImageMobile(resolveHeroUrl(r.data.url_mobile));
      })
      .catch(() => {});
  }, []);

  const TITLE_LINES = lang === "en" ? ["What are we doing", "tonight?"] : ["Qu’est-ce qu’on fait", "ce soir ?"];

  useEffect(() => {
    Promise.all([
      api.get("/establishments", { params: { section: "trending" } }),
      api.get("/establishments", { params: { section: "near" } }),
      api.get("/events"),
      api.get("/categories"),
      api.get("/cities"),
      api.get("/establishments"),
    ])
      .then(([t, n, e, c, v, all]) => {
        setTrending(t.data);
        setNear(n.data);
        setEvents(e.data);
        setCategories(c.data);
        setCities(v.data);
        setAllSpots(all.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const quartierCount = (name) => allSpots.filter((s) => s.neighborhood === name).length;

  return (
    <main data-testid="home-page" className="pb-16">
      <Seo
        title={t("GoSpot — Trouve quoi faire près de toi")}
        siteName="GoSpot"
        description={t("Bars, karaokés, restaurants, soirées, activités, hébergements et événements : trouve quoi faire ce soir à Québec.")}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "GoSpot",
          slogan: t("Trouve quoi faire près de toi."),
        }}
      />

      {/* HERO — image nette, pleine largeur, sans voile ni dégradé blanc */}
      <section className="relative min-h-[420px] overflow-hidden sm:min-h-[540px]">
        <picture>
          {heroImageMobile && heroImageMobile !== heroImage && (
            <source media="(max-width: 639px)" srcSet={heroImageMobile} />
          )}
          <img
            src={heroImage}
            alt={t("Vue panoramique de Québec et du Château Frontenac au coucher du soleil")}
            className="absolute inset-0 h-full w-full object-cover object-[center_12%]"
            style={{ filter: "contrast(1.08) saturate(1.12)" }}
          />
        </picture>
        <div className="relative mx-auto max-w-4xl px-4 pb-24 pt-10 text-center sm:pb-36 sm:pt-14">
          <h1
            className="text-[26px] font-extrabold leading-[1.12] tracking-tight text-slate-950 sm:text-4xl lg:text-5xl"
            style={{ textShadow: "0 1px 3px rgba(255,255,255,0.95), 0 0 26px rgba(255,255,255,0.75), 0 0 48px rgba(255,255,255,0.45)" }}
          >
            {TITLE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-3 text-sm font-bold text-white sm:mt-4 sm:text-lg"
            style={{ textShadow: "0 1px 4px rgba(15,23,42,0.7), 0 2px 12px rgba(15,23,42,0.5), 0 0 22px rgba(15,23,42,0.35)" }}
          >
            {t("Trouve quoi faire près de toi.")}
          </motion.p><motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-6 sm:mt-8"
          >
            <SearchBar cities={cities} />
          </motion.div>
        </div>
      </section>

      {/* CATÉGORIES */}
      <section className="relative z-10 mx-auto -mt-20 mb-14 max-w-5xl px-4 sm:-mt-[104px] sm:mb-[90px]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          className="no-scrollbar flex snap-x gap-3 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible"
        >
          {categories.map((c) => (
            <CategoryChip key={c.key} category={c} />
          ))}
        </motion.div>
      </section>

      {/* PUBLICITÉ */}
      <AdBanner />

      {/* CONTENU PRINCIPAL */}
      <section className="mx-auto mt-6 max-w-[1800px] px-4 sm:mt-8 sm:px-6 lg:px-10">
        <div className="grid gap-6 lg:grid-cols-12">
          <motion.div
            {...reveal}
            data-testid="trending-section"
            className="min-w-0 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5 lg:col-span-7 lg:p-6"
          >
            <SectionHeader icon={Flame} iconColor="#F97316" title="Tendances ce soir" linkTo="/ce-soir" testId="voir-tout-tendances" />
            <div className="no-scrollbar flex snap-x gap-5 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-3">
              {loading
                ? [0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-64 min-w-[220px] animate-pulse rounded-3xl bg-slate-100" />)
                : trending.map((spot) => <SpotCard key={spot.slug} spot={spot} className="min-w-[220px] snap-start sm:min-w-0" />)}
            </div>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            data-testid="near-section"
            className="min-w-0 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5 lg:col-span-5 lg:p-6"
          >
            <SectionHeader icon={MapPin} title="Près de vous" linkTo="/explorer" testId="voir-tout-pres-de-vous" />
            <div className="no-scrollbar flex snap-x gap-5 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible">
              {loading
                ? [0, 1].map((i) => <div key={i} className="h-64 min-w-[220px] animate-pulse rounded-3xl bg-slate-100" />)
                : near.map((spot) => <SpotCard key={spot.slug} spot={spot} className="min-w-[220px] snap-start sm:min-w-0" />)}
            </div>
            <Link
              to="/explorer?vue=carte"
              data-testid="voir-carte-pres-de-vous"
              className="mt-4 flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:bg-[#EFF6FF] hover:text-[#2563EB]"
            >
              <MapIcon className="h-4 w-4" />
              {t("Voir la carte")}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* QUARTIERS */}
      <section className="mx-auto mt-8 max-w-[1800px] px-4 sm:px-6 lg:px-10">
        <motion.div {...reveal} data-testid="quartiers-section">
          <SectionHeader icon={Compass} iconColor="#14B8A6" title="Explorer par quartier" linkTo="/explorer" testId="voir-tout-quartiers" />
          <div className="no-scrollbar flex snap-x gap-5 overflow-x-auto pb-1 sm:grid sm:grid-cols-3 sm:overflow-visible">
            {QUARTIERS.map((q) => (
              <Link
                key={q.name}
                to={`/explorer?quartier=${encodeURIComponent(q.name)}`}
                data-testid={`quartier-${q.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="group relative h-44 min-w-[260px] snap-start overflow-hidden rounded-3xl shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(37,99,235,0.15)] sm:min-w-0"
              >
                <img
                  src={q.image}
                  alt={lang === "en" ? `${q.name} neighbourhood, Quebec City` : `Quartier ${q.name} à Québec`}
                  className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-900/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
                  <div>
                    <p className="text-lg font-extrabold text-white">{q.name}</p>
                    <p className="mt-0.5 text-xs font-medium text-slate-200">
                      {t(q.desc)}
                      {!loading && quartierCount(q.name) > 0 && ` • ${quartierCount(q.name)} spots`}
                    </p>
                  </div>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 backdrop-blur-md transition group-hover:bg-[#2563EB]">
                    <ArrowRight className="h-4 w-4 text-white" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ÉVÉNEMENTS */}
      <section className="mx-auto mt-8 max-w-[1800px] px-4 sm:px-6 lg:px-10">
        <motion.div {...reveal} data-testid="events-section">
          <SectionHeader icon={CalendarDays} title="Événements ce week-end" linkTo="/evenements" testId="voir-tout-evenements" />
          <div className="no-scrollbar flex snap-x gap-5 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible xl:grid-cols-4">
            {loading
              ? [0, 1, 2, 3].map((i) => <div key={i} className="h-28 min-w-[300px] animate-pulse rounded-2xl bg-white" />)
              : events.map((event) => <EventCard key={event.slug} event={event} className="min-w-[300px] snap-start sm:min-w-0" />)}
          </div>
        </motion.div>
      </section>

      {/* AVANTAGES */}
      <section className="mx-auto mt-10 max-w-[1800px] px-4 sm:px-6 lg:px-10">
        <motion.div
          {...reveal}
          data-testid="benefits-section"
          className="grid grid-cols-1 gap-8 rounded-3xl border border-slate-100 bg-white p-8 shadow-sm sm:grid-cols-2 sm:p-10 lg:grid-cols-4"
        >
          {BENEFITS.map((b) => (
            <BenefitCard key={b.title} {...b} testId={`benefit-${b.title.toLowerCase().replace(/[^a-z]/g, "-")}`} />
          ))}
        </motion.div>
      </section>

      <footer className="mx-auto mt-16 max-w-[1800px] border-t border-slate-200 px-4 pt-8 sm:px-6">
        <div className="flex flex-col items-center justify-between gap-3 pb-4 text-sm text-slate-400 sm:flex-row">
          <span className="font-semibold text-slate-500">GoSpot</span>
          <span>{t("Trouve quoi faire près de toi.")} — Québec, Canada</span>
          <Link to="/mobile" data-testid="footer-mobile-link" className="flex items-center gap-1.5 font-semibold text-[#2563EB] transition hover:text-[#1D4ED8]">
            <Smartphone className="h-4 w-4" />
            {t("Application mobile")}
          </Link>
        </div>
      </footer>
    </main>
  );
}
